import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useCobrowse } from '../hooks/useCobrowse';
import { supportSessionLabel } from '../components/common/supportSession';
import './LandingPage.css';
import { dismissUpdateShell } from '../utils/updateShell';

/**
 * Says what this screen is for, once, shortly after it appears.
 *
 * The landing page is the first thing anyone meets and the only thing an
 * unauthenticated visitor can reach — and it announced nothing at all, because
 * every other announcement in this app lives inside AuthGuard. Someone who
 * cannot see the page had a heading, a paragraph of marketing copy and no
 * indication that the way in is a single button below it.
 *
 * Its own region rather than the shared one: this renders outside the Router,
 * so PageAnnouncer — which reads useLocation — cannot mount here.
 *
 * Delayed so it queues behind the browser's own announcement of the document
 * rather than racing it.
 */
function SignInOrientation() {
  const [message, setMessage] = useState('');
  useEffect(() => {
    let cancelled = false;
    const id = setTimeout(() => {
      if (!cancelled) {
        setMessage(
          'Dietech Online. Sign in with the Sign In button in the main section. '
          + 'If you cannot sign in, the page also offers a support session, a phone number and an email address.',
        );
      }
    }, 900);
    return () => { cancelled = true; clearTimeout(id); };
  }, []);
  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
}

export default function LandingPage() {
  const auth = useAuth();

  // Signed out while a new build loaded: no rail or header is coming, so drop
  // their copy now (utils/updateShell.ts) rather than when it times out.
  useEffect(() => {
    dismissUpdateShell();
  }, []);

  // Visitors here are not signed in. The relay accepts customer connections
  // anonymously (agents are the authenticated side), so no token is needed.
  const noToken = useCallback(async () => null, []);
  const cobrowse = useCobrowse(noToken);
  const cobrowseActive = cobrowse.status === 'active';
  const cobrowseBusy =
    cobrowse.status === 'connecting' || cobrowse.status === 'requesting-consent';

  // Allow scrolling on mobile - the global html/body/root overflow:hidden blocks it
  useEffect(() => {
    document.documentElement.classList.add('landing-page-active');
    return () => {
      document.documentElement.classList.remove('landing-page-active');
    };
  }, []);

  return (
    <div className="landing">
      <SignInOrientation />

      {/* Same frame as www.psisupport.com: navy bar, plain cards, one main button. */}
      <header className="landing-header">
        <div className="landing-header-inner">
          <span className="landing-brand">
            <img
              src="/icons/DietechLogo.png"
              alt=""
              className="landing-brand-logo"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <span className="landing-brand-text">
              Dietech
              <span>Dietech Online</span>
            </span>
          </span>
          <nav className="landing-nav" aria-label="Help links">
            <a href="https://www.psisupport.com" className="landing-nav-key">
              Support
            </a>
            <a href="https://www.psisupport.com/download">
              Download<span className="landing-long"> Dietech desktop</span>
            </a>
            <a href="https://status.dietechsoftware.com" target="_blank" rel="noopener noreferrer">
              System status
            </a>
          </nav>
        </div>
      </header>

      <div className="landing-main">
        <main className="landing-grid">
          <section className="landing-card landing-card--signin" aria-labelledby="signin-title">
            <h1 id="signin-title">Sign in to Dietech Online</h1>
            <p className="landing-lede">
              Menus, tray tickets, nutrition and reports for your facility, right in your web browser.
            </p>

            <button
              type="button"
              className="landing-btn landing-btn--primary"
              onClick={() => auth.signinRedirect()}
            >
              Sign In
              <span aria-hidden="true">&rarr;</span>
            </button>

            <h2 className="landing-features-title">What you can do</h2>
            <ul className="landing-features">
              <li>Tray tickets</li>
              <li>Production tallies</li>
              <li>Nutritional analysis</li>
              <li>Table side ordering</li>
              <li>Diet automation</li>
              <li>Reports and charts</li>
              <li>Menu cycles</li>
              <li>EMR integration</li>
            </ul>
          </section>

          <section className="landing-card landing-card--help" aria-labelledby="help-title">
            <h2 id="help-title">Need help signing in?</h2>
            <p className="landing-muted">
              A technician can see this page with you and walk you through it.
            </p>

            {/* Cobrowse support session: lets someone who cannot sign in get
                live help from PSI Support. Points at the in-house relay; the
                old Zoho Assist widget never worked here. */}
            <button
              type="button"
              className="landing-btn landing-btn--secondary"
              onClick={() => {
                if (cobrowseBusy) return;
                // Brings the chat back; ending is in the chat. See supportSessionLabel.
                if (cobrowseActive) cobrowse.openChat();
                else cobrowse.startSession();
              }}
              aria-busy={cobrowseBusy}
            >
              <i className="bi bi-display" aria-hidden="true" />
              {supportSessionLabel(cobrowse.status, cobrowse.sessionCode)}
            </button>

            <ul className="landing-contact">
              <li>
                <i className="bi bi-telephone" aria-hidden="true" />
                <a href="tel:+17165659400">(716) 565-9400, ext. 3</a>
              </li>
              <li>
                <i className="bi bi-envelope" aria-hidden="true" />
                <a href="mailto:support@dietechsoftware.com">support@dietechsoftware.com</a>
              </li>
              <li>
                <i className="bi bi-activity" aria-hidden="true" />
                <a href="https://status.dietechsoftware.com" target="_blank" rel="noopener noreferrer">
                  System status
                </a>
              </li>
            </ul>
          </section>

          <section className="landing-card landing-card--desktop" aria-labelledby="desktop-title">
            <div className="landing-desktop-head">
              <h2 id="desktop-title">Dietech desktop</h2>
            </div>
            <a href="https://www.psisupport.com/download" className="landing-btn landing-btn--download">
              Download Dietech
            </a>
          </section>
        </main>
      </div>

      <footer className="landing-footer">&copy; {new Date().getFullYear()} PSI Buffalo</footer>
    </div>
  );
}
